import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  Alert,
  Snackbar,
  TextField,
  InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PsychologyIcon from '@mui/icons-material/Psychology';
import GroupIcon from '@mui/icons-material/Group';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ThemeBackground from '../Layout/ThemeBackground';
import AvailabilitySlotEditor, { AvailabilitySlot } from '../shared/AvailabilitySlotEditor';
import api from '../../utils/api';
import { fetchAllPublicLookups } from '../../api/lookups';
import { Lookup } from '../../types/lookup';

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  sessionRate?: number;
  startDate?: string;
  episodeCode?: string;
}

interface Counsellor {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dob?: string;
  age?: number;
  availability?: string[];
  sessionTypes?: string[];
  modalities?: string[];
  experienceOrTraining?: string[];
  availabilitySlots?: AvailabilitySlot[];
  currentClients?: Client[];
  currentClientCount?: number;
  maxClients?: number;
  contractSigned?: boolean;
}

interface ClientInfo {
  firstName?: string;
  lastName?: string;
  dob?: string;
  availability?: string[];
  availabilitySlots?: AvailabilitySlot[];
  sessionTypes?: string[];
  personalPreferences?: string;
  gender?: string;
}

const CounsellorAllocationPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [counsellors, setCounsellors] = useState<Counsellor[]>([]);
  const [clientInfo, setClientInfo] = useState<ClientInfo>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCounsellor, setSelectedCounsellor] = useState<string | null>(null);
  const [currentCounsellorId, setCurrentCounsellorId] = useState<string | null>(null);
  const [lookups, setLookups] = useState<{
    availability: Lookup[];
    sessionTypes: Lookup[];
    modality: Lookup[];
  }>({ availability: [], sessionTypes: [], modality: [] });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch counsellors, client preferences, and lookups in parallel
      const [counsellorsResponse, clientResponse, lookupsData] = await Promise.all([
        api.get('/api/counsellors/allocation-details'),
        api.get(`/api/clients/${clientId}`),
        fetchAllPublicLookups()
      ]);
      
      setCounsellors(counsellorsResponse.data);
      const clientData = clientResponse.data;
      setClientInfo({
        firstName: clientData.firstName,
        lastName: clientData.lastName,
        dob: clientData.dob,
        availability: clientData.availability || [],
        availabilitySlots: clientData.availabilitySlots || [],
        sessionTypes: clientData.sessionTypes || [],
        personalPreferences: clientData.assessment?.personalPreferences || '',
        gender: clientData.gender
      });
      // Store current counsellor ID to prevent reassignment to same counsellor
      setCurrentCounsellorId(clientResponse.data.currentCounsellorId || null);
      setLookups(lookupsData);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Unable to load counsellor information. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helper function to expand 'All' or 'Any' to complete list
  const expandAllOrAny = (values: string[] | undefined, lookupCategory: Lookup[]): string[] => {
    if (!values || values.length === 0) return [];
    
    // Check if array contains 'All' or 'Any' (case-insensitive)
    const hasAllOrAny = values.some(v => 
      v.toLowerCase() === 'all' || v.toLowerCase() === 'any'
    );
    
    if (hasAllOrAny) {
      // Return all active lookup values
      return lookupCategory
        .filter(lookup => lookup.active !== false)
        .map(lookup => lookup.value);
    }
    
    return values;
  };

  // Resolve client IDs to lookup values for matching against counsellor values
  const resolveToValues = (ids: string[] | undefined, lookupList: Lookup[]): string[] => {
    if (!ids || ids.length === 0) return [];
    return ids.map(id => {
      const match = lookupList.find(l => l._id === id);
      return match?.value || id;
    });
  };

  // Helper: check if two time ranges overlap
  const timeRangesOverlap = (
    a: { start: string; end: string },
    b: { start: string; end: string }
  ): boolean => {
    return a.start < b.end && b.start < a.end;
  };

  // Helper: calculate overlap minutes between two time ranges
  const getOverlapMinutes = (
    a: { start: string; end: string },
    b: { start: string; end: string }
  ): number => {
    const overlapStart = a.start > b.start ? a.start : b.start;
    const overlapEnd = a.end < b.end ? a.end : b.end;
    if (overlapStart >= overlapEnd) return 0;
    const [sh, sm] = overlapStart.split(':').map(Number);
    const [eh, em] = overlapEnd.split(':').map(Number);
    return (eh * 60 + em) - (sh * 60 + sm);
  };

  const calculateMatchScore = (counsellor: Counsellor): number => {
    let matches = 0;
    let total = 0;

    // Resolve client IDs to values for comparison
    const clientAvailValues = resolveToValues(clientInfo.availability, lookups.availability);
    const clientSessionValues = resolveToValues(clientInfo.sessionTypes, lookups.sessionTypes);

    // Check availability match (day-level)
    if (clientAvailValues.length > 0) {
      const expandedCounsellorAvail = expandAllOrAny(counsellor.availability, lookups.availability);
      const availabilityMatches = clientAvailValues.filter(day => 
        expandedCounsellorAvail.includes(day)
      ).length;
      matches += availabilityMatches;
      total += clientAvailValues.length;
    }

    // Check session type match
    if (clientSessionValues.length > 0) {
      const expandedCounsellorSessions = expandAllOrAny(counsellor.sessionTypes, lookups.sessionTypes);
      const sessionTypeMatches = clientSessionValues.filter(type => 
        expandedCounsellorSessions.includes(type)
      ).length;
      matches += sessionTypeMatches;
      total += clientSessionValues.length;
    }

    // Check availability time slot overlap (day + time range matching)
    const clientSlots = clientInfo.availabilitySlots || [];
    const counsellorSlots = counsellor.availabilitySlots || [];

    if (clientSlots.length > 0 && counsellorSlots.length > 0) {
      let slotDayMatches = 0;
      let slotTimeOverlapScore = 0;

      for (const clientSlot of clientSlots) {
        const counsellorSlot = counsellorSlots.find(
          cs => cs.day.toLowerCase() === clientSlot.day.toLowerCase()
        );

        if (counsellorSlot) {
          // Day match found
          slotDayMatches++;

          // Check time range overlaps for this day
          let dayHasOverlap = false;
          for (const clientRange of clientSlot.timeRanges) {
            for (const counsellorRange of counsellorSlot.timeRanges) {
              if (timeRangesOverlap(clientRange, counsellorRange)) {
                dayHasOverlap = true;
                const overlapMins = getOverlapMinutes(clientRange, counsellorRange);
                // Each 60 minutes of overlap counts as a full point
                slotTimeOverlapScore += Math.min(overlapMins / 60, 1);
              }
            }
          }

          // Bonus: if day matched but no time overlap, still partial credit
          if (!dayHasOverlap) {
            slotTimeOverlapScore += 0.25;
          }
        }
      }

      // Weight: time slot matching counts as equivalent to 2 day-level matches
      const slotWeight = 2;
      const maxSlotScore = clientSlots.length * slotWeight;
      const actualSlotScore = (slotDayMatches > 0 ? slotTimeOverlapScore : 0) * slotWeight;

      matches += actualSlotScore;
      total += maxSlotScore;
    }

    return total > 0 ? Math.round((matches / total) * 100) : 0;
  };

  const isAvailabilityMatch = (day: string): boolean => {
    const clientAvailValues = resolveToValues(clientInfo.availability, lookups.availability);
    return clientAvailValues.includes(day);
  };

  const isSessionTypeMatch = (type: string): boolean => {
    const clientSessionValues = resolveToValues(clientInfo.sessionTypes, lookups.sessionTypes);
    return clientSessionValues.includes(type);
  };

  const calculateAge = (dob: string): number => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleAssign = async (counsellorId: string) => {
    if (!clientId) return;
    
    // Check if trying to reassign to current counsellor
    if (currentCounsellorId && counsellorId === currentCounsellorId) {
      setSnackbar({
        open: true,
        message: 'This client is already assigned to this counsellor',
        severity: 'error'
      });
      return;
    }
    
    try {
      setAssigning(true);
      setSelectedCounsellor(counsellorId);
      
      await api.post(`/api/clients/${clientId}/assign-counsellor`, {
        counsellorId
      });
      
      setSnackbar({
        open: true,
        message: 'Client assigned successfully!',
        severity: 'success'
      });
      
      // Navigate after a brief delay to show the success message
      setTimeout(() => {
        navigate(`/clients/${clientId}`);
      }, 1500);
    } catch (error: any) {
      console.error('Error assigning counsellor:', error);
      setError(error.response?.data?.error || 'Failed to assign counsellor. Please try again.');
    } finally {
      setAssigning(false);
      setSelectedCounsellor(null);
    }
  };

  // Resolve a lookup value (could be _id or value) to its display name
  const resolveDisplayName = (val: string, lookupList: Lookup[]): string => {
    const match = lookupList.find(l => l._id === val || l.value === val);
    return match?.displayName || val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const filteredCounsellors = counsellors.filter(counsellor => {
    const searchLower = searchQuery.trim().toLowerCase();
    if (!searchLower) return true;
    return (
      `${counsellor.firstName} ${counsellor.lastName}`.toLowerCase().includes(searchLower) ||
      (counsellor.email?.toLowerCase().includes(searchLower) ?? false)
    );
  });

  if (loading) {
    return (
      <ThemeBackground variant="full">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
        </Box>
      </ThemeBackground>
    );
  }

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8">
        <div className="glass-card p-6">
          {/* Header */}
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(`/clients/${clientId}`)}
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Back to Client
              </Button>
              <Typography variant="h4" className="theme-text-primary" sx={{ fontWeight: 700 }}>
                Select Counsellor for Allocation
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Client Summary Panel */}
          <Card
            sx={{
              backgroundColor: 'rgba(33, 150, 243, 0.06)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(33, 150, 243, 0.25)',
              borderRadius: 2,
              mb: 3
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InfoOutlinedIcon sx={{ color: 'rgba(33, 150, 243, 0.9)' }} />
                <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                  Client Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              
              <Grid container spacing={2}>
                {/* Name & Age */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PersonIcon sx={{ color: 'rgba(100, 181, 246, 0.9)', fontSize: 20 }} />
                    <Typography variant="subtitle2" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                      Name & Age
                    </Typography>
                  </Box>
                  <Typography variant="body2" className="theme-text-primary" sx={{ fontWeight: 500 }}>
                    {clientInfo.firstName} {clientInfo.lastName}
                  </Typography>
                  {clientInfo.dob && (
                    <Typography variant="caption" className="theme-text-muted">
                      Age: {calculateAge(clientInfo.dob)}{clientInfo.gender ? ` • ${clientInfo.gender}` : ''}
                    </Typography>
                  )}
                </Grid>

                {/* Session Types */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PsychologyIcon sx={{ color: 'rgba(255, 152, 0, 0.9)', fontSize: 20 }} />
                    <Typography variant="subtitle2" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                      Session Types
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {clientInfo.sessionTypes && clientInfo.sessionTypes.length > 0 ? (
                      clientInfo.sessionTypes.map((type, idx) => (
                        <Chip
                          key={idx}
                          label={resolveDisplayName(type, lookups.sessionTypes)}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(33, 150, 243, 0.2)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(33, 150, 243, 0.4)',
                            fontSize: '0.7rem'
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="caption" className="theme-text-muted">Not specified</Typography>
                    )}
                  </Box>
                </Grid>

                {/* Availability */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccessTimeIcon sx={{ color: 'rgba(76, 175, 80, 0.9)', fontSize: 20 }} />
                    <Typography variant="subtitle2" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                      Availability
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {clientInfo.availability && clientInfo.availability.length > 0 ? (
                      clientInfo.availability.map((day, idx) => (
                        <Chip
                          key={idx}
                          label={resolveDisplayName(day, lookups.availability)}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(76, 175, 80, 0.2)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(76, 175, 80, 0.4)',
                            fontSize: '0.7rem'
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="caption" className="theme-text-muted">Not specified</Typography>
                    )}
                  </Box>
                </Grid>

                {/* Personal Preferences */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <FavoriteBorderIcon sx={{ color: 'rgba(233, 30, 99, 0.9)', fontSize: 20 }} />
                    <Typography variant="subtitle2" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                      Personal Preferences
                    </Typography>
                  </Box>
                  <Typography variant="body2" className="theme-text-muted" sx={{ whiteSpace: 'pre-wrap' }}>
                    {clientInfo.personalPreferences || 'None specified'}
                  </Typography>
                </Grid>

                {/* Client Time Slots */}
                {clientInfo.availabilitySlots && clientInfo.availabilitySlots.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AccessTimeIcon sx={{ color: 'rgba(100, 181, 246, 0.9)', fontSize: 20 }} />
                      <Typography variant="subtitle2" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                        Specific Time Slots
                      </Typography>
                    </Box>
                    <AvailabilitySlotEditor
                      slots={clientInfo.availabilitySlots}
                      onChange={() => {}}
                      readOnly
                      compact
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Counsellor Search */}
          <TextField
            fullWidth
            placeholder="Search counsellors by name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.6)' }} />
                </InputAdornment>
              )
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                color: 'rgba(255, 255, 255, 0.9)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(33, 150, 243, 0.7)' }
              }
            }}
          />

          {/* Counsellors Grid */}
          <Grid container spacing={3}>
            {filteredCounsellors.length === 0 ? (
              <Grid item xs={12}>
                <Typography className="theme-text-muted" align="center" sx={{ py: 4 }}>
                  {counsellors.length === 0
                    ? 'No available counsellors found.'
                    : 'No counsellors match your search.'}
                </Typography>
              </Grid>
            ) : (
              filteredCounsellors.map((counsellor) => {
                const isAtCapacity = (counsellor.currentClientCount ?? 0) >= (counsellor.maxClients ?? 0);
                const isCurrentCounsellor = currentCounsellorId === counsellor._id;
                const age = counsellor.dob ? calculateAge(counsellor.dob) : counsellor.age;
                const matchScore = calculateMatchScore(counsellor);
                const hasHighMatch = matchScore >= 70;
                const hasMediumMatch = matchScore >= 40 && matchScore < 70;

                return (
                  <Grid item xs={12} md={6} key={counsellor._id}>
                    <Card
                      sx={{
                        backgroundColor: hasHighMatch 
                          ? 'rgba(76, 175, 80, 0.08)' 
                          : hasMediumMatch 
                          ? 'rgba(255, 152, 0, 0.08)'
                          : 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        border: hasHighMatch
                          ? '2px solid rgba(76, 175, 80, 0.5)'
                          : hasMediumMatch
                          ? '2px solid rgba(255, 152, 0, 0.5)'
                          : '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: hasHighMatch 
                            ? 'rgba(76, 175, 80, 0.12)' 
                            : hasMediumMatch 
                            ? 'rgba(255, 152, 0, 0.12)'
                            : 'rgba(255, 255, 255, 0.08)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          {/* Counsellor Basic Info */}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                              <PersonIcon sx={{ color: 'rgba(100, 181, 246, 0.9)' }} />
                              <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                                {counsellor.firstName} {counsellor.lastName}
                              </Typography>
                              {!counsellor.contractSigned && (
                                <Chip
                                  label="Contract Not Signed"
                                  size="small"
                                  sx={{
                                    backgroundColor: 'rgba(239, 83, 80, 0.2)',
                                    color: 'rgba(239, 83, 80, 1)',
                                    border: '1px solid rgba(239, 83, 80, 0.5)',
                                    fontWeight: 600,
                                    fontSize: '0.7rem'
                                  }}
                                />
                              )}
                              {matchScore > 0 && (
                                <Chip
                                  label={`${matchScore}% Match`}
                                  size="small"
                                  sx={{
                                    backgroundColor: hasHighMatch 
                                      ? 'rgba(76, 175, 80, 0.3)' 
                                      : hasMediumMatch 
                                      ? 'rgba(255, 152, 0, 0.3)'
                                      : 'rgba(100, 181, 246, 0.3)',
                                    color: 'rgba(255, 255, 255, 0.95)',
                                    border: hasHighMatch
                                      ? '1px solid rgba(76, 175, 80, 0.6)'
                                      : hasMediumMatch
                                      ? '1px solid rgba(255, 152, 0, 0.6)'
                                      : '1px solid rgba(100, 181, 246, 0.6)',
                                    fontWeight: 700,
                                    fontSize: '0.75rem'
                                  }}
                                />
                              )}
                            </Box>
                            
                            <Typography variant="body2" className="theme-text-muted" sx={{ mb: 0.5 }}>
                              {counsellor.email}
                            </Typography>
                            {counsellor.phone && (
                              <Typography variant="body2" className="theme-text-muted" sx={{ mb: 1 }}>
                                {counsellor.phone}
                              </Typography>
                            )}
                            
                            {age && (
                              <Chip
                                label={`Age: ${age}`}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(156, 39, 176, 0.2)',
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  border: '1px solid rgba(156, 39, 176, 0.3)',
                                  mt: 1
                                }}
                              />
                            )}

                            {/* Client Load */}
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" className="theme-text-muted">
                                Client Load: {counsellor.currentClientCount ?? 0} / {counsellor.maxClients ?? 'N/A'}
                              </Typography>
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 8,
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  borderRadius: 4,
                                  overflow: 'hidden',
                                  mt: 0.5
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${Math.min(((counsellor.currentClientCount ?? 0) / (counsellor.maxClients ?? 1)) * 100, 100)}%`,
                                    height: '100%',
                                    backgroundColor: isAtCapacity
                                      ? '#f44336'
                                      : (counsellor.currentClientCount ?? 0) / (counsellor.maxClients ?? 1) > 0.8
                                      ? '#ff9800'
                                      : '#4caf50',
                                    transition: 'all 0.3s ease'
                                  }}
                                />
                              </Box>
                            </Box>
                          </Grid>

                          {/* Availability */}
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <CalendarTodayIcon sx={{ color: 'rgba(76, 175, 80, 0.9)', fontSize: 20 }} />
                              <Typography variant="subtitle2" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                                Availability
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {(() => {
                                const expandedAvailability = expandAllOrAny(counsellor.availability, lookups.availability);
                                return expandedAvailability.length > 0 ? (
                                  expandedAvailability.map((day, idx) => {
                                    const isMatch = isAvailabilityMatch(day);
                                    return (
                                      <Chip
                                        key={idx}
                                        label={resolveDisplayName(day, lookups.availability)}
                                        size="small"
                                        sx={{
                                          backgroundColor: isMatch ? 'rgba(76, 175, 80, 0.4)' : 'rgba(76, 175, 80, 0.2)',
                                          color: 'rgba(255, 255, 255, 0.9)',
                                          border: isMatch ? '2px solid rgba(76, 175, 80, 0.8)' : '1px solid rgba(76, 175, 80, 0.3)',
                                          fontSize: '0.7rem',
                                          fontWeight: isMatch ? 700 : 400,
                                          boxShadow: isMatch ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                          transform: isMatch ? 'scale(1.05)' : 'scale(1)',
                                          transition: 'all 0.2s ease'
                                        }}
                                      />
                                    );
                                  })
                                ) : (
                                  <Typography variant="caption" className="theme-text-muted">
                                    Not specified
                                  </Typography>
                                );
                              })()}
                            </Box>
                          </Grid>

                          {/* Counsellor Time Slots */}
                          {counsellor.availabilitySlots && counsellor.availabilitySlots.length > 0 && (
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <AccessTimeIcon sx={{ color: 'rgba(100, 181, 246, 0.9)', fontSize: 18 }} />
                                <Typography variant="subtitle2" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                                  Time Slots
                                </Typography>
                              </Box>
                              <AvailabilitySlotEditor
                                slots={counsellor.availabilitySlots}
                                onChange={() => {}}
                                readOnly
                                compact
                              />
                            </Grid>
                          )}

                          {/* Session Types & Modalities */}
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <PsychologyIcon sx={{ color: 'rgba(255, 152, 0, 0.9)', fontSize: 20 }} />
                              <Typography variant="subtitle2" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                                Preferences & Modalities
                              </Typography>
                            </Box>
                            
                            {/* Session Types */}
                            {counsellor.sessionTypes && counsellor.sessionTypes.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" className="theme-text-muted" sx={{ display: 'block', mb: 0.5 }}>
                                  Session Types:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {expandAllOrAny(counsellor.sessionTypes, lookups.sessionTypes).map((type, idx) => {
                                    const isMatch = isSessionTypeMatch(type);
                                    return (
                                      <Chip
                                        key={idx}
                                        label={resolveDisplayName(type, lookups.sessionTypes)}
                                        size="small"
                                        sx={{
                                          backgroundColor: isMatch ? 'rgba(33, 150, 243, 0.4)' : 'rgba(33, 150, 243, 0.2)',
                                          color: 'rgba(255, 255, 255, 0.9)',
                                          border: isMatch ? '2px solid rgba(33, 150, 243, 0.8)' : '1px solid rgba(33, 150, 243, 0.3)',
                                          fontSize: '0.7rem',
                                          fontWeight: isMatch ? 700 : 400,
                                          boxShadow: isMatch ? '0 0 8px rgba(33, 150, 243, 0.6)' : 'none',
                                          transform: isMatch ? 'scale(1.05)' : 'scale(1)',
                                          transition: 'all 0.2s ease'
                                        }}
                                      />
                                    );
                                  })}
                                </Box>
                              </Box>
                            )}

                            {/* Modalities */}
                            {counsellor.modalities && counsellor.modalities.length > 0 && (
                              <Box>
                                <Typography variant="caption" className="theme-text-muted" sx={{ display: 'block', mb: 0.5 }}>
                                  Modalities:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {expandAllOrAny(counsellor.modalities, lookups.modality).map((modality, idx) => (
                                    <Chip
                                      key={idx}
                                      label={resolveDisplayName(modality, lookups.modality)}
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(255, 152, 0, 0.2)',
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid rgba(255, 152, 0, 0.3)',
                                        fontSize: '0.7rem'
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Grid>

                          {/* Experience/Training */}
                          {counsellor.experienceOrTraining && counsellor.experienceOrTraining.length > 0 && (
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PsychologyIcon sx={{ color: 'rgba(156, 39, 176, 0.9)', fontSize: 20 }} />
                                <Typography variant="subtitle2" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                                  Experience / Training
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {counsellor.experienceOrTraining.map((item, idx) => (
                                  <Chip
                                    key={idx}
                                    label={item}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(156, 39, 176, 0.2)',
                                      color: 'rgba(255, 255, 255, 0.9)',
                                      border: '1px solid rgba(156, 39, 176, 0.3)',
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Grid>
                          )}

                          {/* Current Clients */}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <GroupIcon sx={{ color: 'rgba(233, 30, 99, 0.9)', fontSize: 20 }} />
                              <Typography variant="subtitle2" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                                Current Clients ({counsellor.currentClients?.length ?? 0})
                              </Typography>
                            </Box>
                            
                            {counsellor.currentClients && counsellor.currentClients.length > 0 ? (
                              <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                                {counsellor.currentClients.map((client, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      mb: 1,
                                      p: 1,
                                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                      borderRadius: 1,
                                      border: '1px solid rgba(255, 255, 255, 0.05)'
                                    }}
                                  >
                                    <Typography variant="caption" className="theme-text-primary" sx={{ fontWeight: 500 }}>
                                      {client.firstName} {client.lastName}
                                    </Typography>
                                    {client.episodeCode && (
                                      <Typography variant="caption" className="theme-text-muted" sx={{ display: 'block' }}>
                                        Code: {client.episodeCode}
                                      </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                      {client.sessionRate && (
                                        <Typography variant="caption" className="theme-text-muted">
                                          £{client.sessionRate}
                                        </Typography>
                                      )}
                                      {client.startDate && (
                                        <Typography variant="caption" className="theme-text-muted">
                                          Started: {formatDate(client.startDate)}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" className="theme-text-muted">
                                No current clients
                              </Typography>
                            )}
                          </Grid>

                          {/* Action Button */}
                          <Grid item xs={12}>
                            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center' }}>
                              {isCurrentCounsellor && (
                                <Chip
                                  label="Currently Assigned"
                                  size="small"
                                  sx={{
                                    backgroundColor: 'rgba(33, 150, 243, 0.3)',
                                    color: 'rgba(255, 255, 255, 0.95)',
                                    border: '1px solid rgba(33, 150, 243, 0.6)',
                                    fontWeight: 700
                                  }}
                                />
                              )}
                              <Button
                                variant="contained"
                                onClick={() => handleAssign(counsellor._id)}
                                disabled={isAtCapacity || isCurrentCounsellor || (assigning && selectedCounsellor === counsellor._id)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  minWidth: 180,
                                  backgroundColor: isCurrentCounsellor ? 'rgba(33, 150, 243, 0.2)' : isAtCapacity ? 'rgba(244, 67, 54, 0.3)' : 'rgba(76, 175, 80, 0.2)',
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  border: `1px solid ${isCurrentCounsellor ? 'rgba(33, 150, 243, 0.3)' : isAtCapacity ? 'rgba(244, 67, 54, 0.5)' : 'rgba(76, 175, 80, 0.3)'}`,
                                  '&:hover': {
                                    backgroundColor: isCurrentCounsellor ? 'rgba(33, 150, 243, 0.3)' : isAtCapacity ? 'rgba(244, 67, 54, 0.4)' : 'rgba(76, 175, 80, 0.3)',
                                    borderColor: isCurrentCounsellor ? 'rgba(33, 150, 243, 0.5)' : isAtCapacity ? 'rgba(244, 67, 54, 0.7)' : 'rgba(76, 175, 80, 0.5)'
                                  },
                                  '&:disabled': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    color: 'rgba(255, 255, 255, 0.3)',
                                    borderColor: 'rgba(255, 255, 255, 0.1)'
                                  }
                                }}
                              >
                                {assigning && selectedCounsellor === counsellor._id ? (
                                  <CircularProgress size={24} sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                                ) : isCurrentCounsellor ? (
                                  'Already Assigned'
                                ) : isAtCapacity ? (
                                  'At Capacity'
                                ) : (
                                  'Assign to This Counsellor'
                                )}
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </div>
      </div>
    </ThemeBackground>
  );
};
  
  export default CounsellorAllocationPage;
